import type { Game } from '@playgame/types'
import { EVENT_TOGGLE_SOUND, EVENT_FOCUS_OUT, EVENT_FOCUS_IN, EVENT_TOGGLE_DRAW } from '@playgame/constants'
import ProviderService from '@playgame/utils/ProviderService'
import { usePlaygameView, usePlaygameConfig, useDeposit, usePlaygameState, usePlaygameMessage, usePlaygameEventTrigger, useMobileApp, navigateTo, useI18n, nextTick } from '#imports'

export const useGameManager = () => {
    const { t } = useI18n()
    // Writes focusedGame via the private `_writableState` handle rather than
    // useStateActions, which would create a circular loop
    // (useStateActions → useRealityCheck → useGameManager).
    const { state, games, gamesArray, getGame, removeGameFromState, _writableState } = usePlaygameState()
    const { clearMessage, addMessage } = usePlaygameMessage()
    const { eventTrigger } = usePlaygameEventTrigger()
    const { requestDataObject } = useDeposit()

    const { multipleGamesAllowed } = usePlaygameConfig()

    const { isMobileView, isTabletPortrait } = usePlaygameView()
    const { isMobileApp } = useMobileApp()

    const addGame = (game: Game) => {
        games.value.set(game.identifier, game)
    }

    const reloadGame = async (slug: string, identifier?: string, instance?: string | null) => {
        const game = identifier ? getGame(identifier) : undefined

        clearMessage()

        if (game) {
            // Close game before reloading
            await removeGame(game.identifier)
        }

        // Trigger new game load
        return navigateTo({
            query: {
                start: slug,
                gameInstanceId: instance
            }
        }, { replace: true })
    }

    const removeGame = async (identifier: string, isClosed: boolean = false) => {
        const game = identifier ? getGame(identifier) : undefined

        if (game) {
            // Dispatch the end request before the teardown destroys the iframe;
            // await it last so a slow roundtrip never delays the visual close.
            let closing: Promise<void> | undefined

            if (!isClosed) {
                // Failures are already reported by useOpenapi; the handler keeps a
                // throwing cleanup() below from leaving the promise unhandled.
                closing = game.gameWrapper?.closeGame?.().catch(() => {})
            }

            // Closed before EVENT_GAME_READY, so nothing else clears the flag.
            if (!game.ready) _writableState.value.gameStarting = false

            game.cleanup?.()
            removeGameFromState(identifier)

            await nextTick()

            if (gamesArray.value.length) {
                const nextGame = gamesArray.value[gamesArray.value.length - 1]

                if (nextGame) {
                    setFocusedGame(nextGame.identifier)
                }
            }

            await closing
        }

        if (multipleGamesAllowed && !gamesArray.value.length && (!isMobileView.value || isTabletPortrait.value)) {
            pushToNewGameState()
        }

        clearMessage()
    }

    const closePlaygame = async (forceClose = false) => {
        const confirmDialog = (): Promise<boolean> => {
            if (forceClose) return Promise.resolve(true)

            return new Promise((resolve) => {
                addMessage({
                    type: 'info',
                    header: t('playgame.closeGameHeader'),
                    message: t('playgame.closeGameMessage'),
                    pauseGame: true,
                    actions: {
                        close: {
                            action: () => {
                                clearMessage()
                                resolve(true)
                            },
                            color: 'primary',
                            text: t('playgame.closeGame'),
                        },
                        dismiss: {
                            action: () => {
                                clearMessage()
                                resolve(false)
                            },
                            color: 'secondary',
                            outlined: true,
                            text: t('playgame.continuePlaying'),
                        }
                    },
                    dismissAction: 'dismiss',
                })
            })
        }

        // Make the confirm dialog promise based to ensure code awaits the result before executing
        const hasConfirmed = isMobileApp ? true : await confirmDialog()

        if (hasConfirmed) {
            try {
                const gameIds = gamesArray.value.map(game => game.identifier)
                for (const id of gameIds) {
                    await removeGame(id)
                }
            }
            finally {
                if (!window.opener) {
                    // Not `/`: it only redirects a player to the lobby. External, so the provider scripts go with the page.
                    await navigateTo(t('pages.lobby'), { external: true })
                }
                else {
                    window.close()
                }
            }
        }
    }

    const pushToNewGameState = async () => {
        // Prevent redirect for ongoing payment
        if (requestDataObject.value) return

        await navigateTo({ query: { new: '1' } })
    }

    // Sets focusedGame and fires provider focus/draw events; for a plain write use useStateActions.setFocusedGameId.
    const setFocusedGame = (gameId: string) => {
        _writableState.value.focusedGame = gameId

        if (gamesArray.value.length) {
            for (const game of gamesArray.value) {
                if (game.identifier !== gameId) {
                    eventTrigger(game.identifier, EVENT_FOCUS_OUT, { enabled: false })
                }
            }
        }

        eventTrigger(gameId, EVENT_FOCUS_IN, { enabled: true })
        eventTrigger(gameId, EVENT_TOGGLE_DRAW, { enabled: true })

        const game = getGame(gameId)

        if (game?.ready && ProviderService.isMagnet(game?.providerId)) {
            const soundEnabled = state.value.sound

            eventTrigger(gameId, EVENT_TOGGLE_SOUND, { enabled: soundEnabled })

            return
        }
    }

    return {
        addGame,
        reloadGame,
        removeGame,
        closePlaygame,
        pushToNewGameState,
        setFocusedGame,
    }
}
